import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import Card from '../../../components/Card';
import { FieldSet, KeyValue, Spinner } from '../../../elements';
import Axios, { AxiosError } from 'axios';
import { match } from 'react-router-dom';
import { History } from 'history';
import { API_URL } from '../../../../lib/const';
import { IApplication, ICoordinationDocumentGroup } from '../types';
import formater from '../../common/formater';
import DocumentsView from '../../../enrollment/components/DocumentsView';
import { IDocument, IDocumentType } from '../../common/types';
import TransferApplicationDocumentAnalysisModal from '../components/TransferApplicationDocumentAnalysisModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { PermissionContext } from '../../../../hoc/withPermission';
import CreateEnrollmentModal from '../components/CreateEnrollmentModal';
import CreateIssueModal from '../components/CreateIssueModal';
import CoordinationIssueModal, { TCoordinationIssue } from '../components/CoordinationIssueModal';
import AppropriationAlert from '../../../enrollment/components/AppropriationAlert';
import AppropriationHistory from '../../../enrollment/components/AppropriationHistory';
import { UserContext } from '../../common/states/UserState';
import {
  createTransferDocumentAppropriation,
  fetchEligibleTransferAppropriationUsers,
  fetchTransferDocumentAppropriations,
  transferTransferDocumentAppropriation,
} from '../services/transferDocumentAppropriationService';
import {
  activeAppropriation,
  canReviewDocuments,
  canTransferAppropriation,
} from '../components/appropriationState';
import { ITransferDocumentAppropriation } from '../types';
import { issueLabel } from '../components/issueLabel';
import CoordinationDocumentCard from '../components/CoordinationDocumentCard';
import { fetchCoordinationDocuments, updateTransferRegistration } from '../services/transferApplicationService';
import consts from '../consts';

const finalReviewLabel = (application: IApplication) => {
  if (!application.coordination_sent_at) {
    return 'candidato ainda não enviado para a Coordenação.';
  }

  switch (application.coordination_documents_step_status) {
    case 'Em análise':
      return 'a Coordenação enviou os documentos, aguardando a sua análise.';
    case 'Com pendências':
      return 'pendência aberta para a Coordenação.';
    case 'Aprovado':
      return 'aprovada. Fluxo encerrado.';
    default:
      return 'aguardando a Coordenação inserir os documentos e enviar para a análise final.';
  }
};

const TransferApplicationDocumentReviewPage = ({ history, match }: { history: History, match: match<{ token: string }> }) => {
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState<IApplication>();
  const [requestedDocuments, setRequestedDocuments] = useState<IDocumentType[]>([]);
  const [showEnrollmentCreationModal, setShowEnrollmentCreationModal] = useState(false);
  const [showCreateIssueModal, setShowCreateIssueModal] = useState(false);
  const [showCoordinationIssueModal, setShowCoordinationIssueModal] = useState(false);
  const [approvingCoordination, setApprovingCoordination] = useState(false);
  const [submiting, setSubmitting] = useState(false);
  const [documentUnderReview, setDocumentUnderReview] = useState<{ document: IDocument, result: 'Approved' | 'Reproved' }>();
  const [appropriations, setAppropriations] = useState<ITransferDocumentAppropriation[]>([]);
  const [loadingAppropriation, setLoadingAppropriation] = useState(false);
  const [coordinationDocuments, setCoordinationDocuments] = useState<ICoordinationDocumentGroup[]>([]);
  const [isEditingRegistration, setIsEditingRegistration] = useState(false);
  const [savingRegistration, setSavingRegistration] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({ real_name: '', assumed_name: '', email: '' });
  const { user } = useContext(UserContext) as any;
  const token = match.params.token;

  const currentAppropriation = useMemo(
    () => activeAppropriation(appropriations),
    [appropriations],
  );
  const isAppropriatedByMe = canReviewDocuments(currentAppropriation, user);
  const canTransfer = canTransferAppropriation(currentAppropriation, user);

  const analysisResult = useMemo(() => {
    const result = { docs: { done: false, value: false }, issues: { done: true, value: false }, application: { value: false, done: false } }

    if (!application) return result;

    result.issues.value = !application.issues.some(i => i.status === 'Criada')
    result.docs.done = !application.documents.some(d => d.approved === null)
    result.docs.value = !application.documents.some(d => [false, null].includes(d.approved))

    result.application.done = result.issues.done && result.docs.done
    result.application.value = result.issues.value && result.docs.value

    return result;

  }, [application && application.issues, application && application.documents])

  const fetchApplication = () => {
    setLoading(true);

    Axios.get(`${API_URL}/transfers/applications/${token}`)
      .then(response => setApplication(response.data))
      .catch((error: AxiosError) => {
        if (error.response && error.response.status === 404) {
          history.push('/transferencias/aplicacoes');
        }
      })
      .finally(() => setLoading(false))
  }

  const fetchAppropriationData = useCallback((applicationId: number) => {
    setLoadingAppropriation(true);
    fetchTransferDocumentAppropriations(applicationId)
      .then(data => setAppropriations(data || []))
      .catch(() => toast.error('Não foi possível carregar as apropriações.'))
      .finally(() => setLoadingAppropriation(false));
  }, []);

  const fetchCoordinationDocumentHistory = useCallback(async () => {
    if (!token) return;

    try {
      const groups = await fetchCoordinationDocuments(token);
      setCoordinationDocuments(groups || []);
    } catch (error) {
      toast.error('Não foi possível carregar os documentos da Coordenação.');
    }
  }, [token]);

  useEffect(() => { if (token) fetchApplication() }, [token])
  useEffect(() => { if (token) fetchCoordinationDocumentHistory() }, [token, fetchCoordinationDocumentHistory])

  useEffect(() => {
    setIsEditingRegistration(false);
    setSavingRegistration(false);
    setRegistrationForm({ real_name: '', assumed_name: '', email: '' });
  }, [token])

  useEffect(() => {
    if (application) fetchAppropriationData(application.id);
  }, [application && application.id, fetchAppropriationData]);

  useEffect(() => {
    if (!application) return;

    Axios.get(`${API_URL}/transfers/processes/${application.process.id}/required_documents`)
      .then(response => setRequestedDocuments(response.data)
      )
  }, [application && application.id])

  const handleReviewDocuments = useCallback(() => {
    if (!application) return
    setSubmitting(true);

    const result = analysisResult.application.value ? 'Approved' : 'Reproved'

    Axios.post(`${API_URL}/transfers/applications/${application.token}/document_data_review`, { result })
      .then(() => {
        fetchApplication();
        fetchAppropriationData(application.id);
      })
      .catch(error => {
        const response = (error as AxiosError).response;

        if (response && response.status === 400) {
          const errors = response.data;

          if (errors.non_field_errors) {
            toast.error(errors.non_field_errors.join(','))

          } else {
            toast.error('Ocorreu um erro ao analisar o documento.')

          }

        } else {
          toast.error('Ocorreu um erro desconhecido ao analisar o documento.')

        }
      })
      .finally(() => setSubmitting(false))

  }, [application])

  const handleCreateEnrollment = useCallback(async () => {
    if (!application) return

    Axios.post(`${API_URL}/transfers/applications/promote`, { applications: [application.id] })
      .then(() => fetchApplication())
      .catch(error => {
        const response = (error as AxiosError).response;

        if (response && response.status === 400) {
          const errors = response.data;

          toast.error(errors.applications[application.id])

        } else {
          toast.error('Ocorreu um erro desconhecido ao criar matricula.')

        }
      })
      .finally(() => setShowEnrollmentCreationModal(false))

  }, [application])

  const handleCreateIssue = useCallback((content: string) => {
    if (!application) return;

    Axios.post(`${API_URL}/transfers/applications/${application.token}/issues`, { content, category: 'Documental' })
      .then(() => {
        fetchApplication();
        setShowCreateIssueModal(false);
      })
      .catch(error => {
        const response = (error as AxiosError).response;

        if (response && response.status === 400) {
          const errors = response.data;
          toast.error(errors.content[0])
        }
      })

  }, [application])

  // Análise final: a Secretaria confere a adaptação e a escala que a Coordenação inseriu.
  const handleCreateCoordinationIssue = useCallback((issue: TCoordinationIssue) => {
    if (!application) return Promise.resolve();

    return Axios.post(`${API_URL}/transfers/applications/${application.token}/issues`, {
      ...issue,
      category: 'Documentos da Coordenação',
    })
      .then(() => {
        fetchApplication();
        setShowCoordinationIssueModal(false);
        toast.success('Pendência enviada por e-mail para a Coordenação.');
      })
      .catch(error => {
        const response = (error as AxiosError).response;
        const message = response && response.data && response.data.content && response.data.content[0];
        toast.error(message || 'Não foi possível abrir a pendência para a Coordenação.');
      })
  }, [application])

  const handleApproveCoordinationDocuments = useCallback(() => {
    if (!application) return;

    setApprovingCoordination(true);
    Axios.post(`${API_URL}/transfers/applications/${application.token}/approve_coordination_documents`)
      .then(() => {
        toast.success('Documentos da Coordenação aprovados. Fluxo encerrado.');
        fetchApplication();
      })
      .catch(error => {
        const response = (error as AxiosError).response;
        const message = response && response.data && response.data.non_field_errors && response.data.non_field_errors[0];
        toast.error(message || 'Não foi possível aprovar os documentos da Coordenação.');
      })
      .finally(() => setApprovingCoordination(false))
  }, [application])

  const handleSaveRegistration = useCallback(() => {
    if (!application) return;

    if (!registrationForm.real_name.trim()) {
      toast.error('O nome é obrigatório.');
      return;
    }

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(registrationForm.email)) {
      toast.error('Informe um e-mail válido.');
      return;
    }

    setSavingRegistration(true);
    updateTransferRegistration(application.token, registrationForm)
      .then(() => {
        toast.success('Cadastro atualizado com sucesso.');
        setIsEditingRegistration(false);
        fetchApplication();
      })
      .catch((error: AxiosError) => {
        const response = error.response;

        if (response && response.status === 400) {
          toast.error(Object.values(response.data).flat().join(', ') || 'Ocorreu um erro ao atualizar o cadastro.');
        } else {
          toast.error('Ocorreu um erro ao atualizar o cadastro.');
        }
      })
      .finally(() => setSavingRegistration(false));
  }, [application, registrationForm]);

  const handleAppropriate = useCallback(() => {
    if (!application) return;
    setLoadingAppropriation(true);
    createTransferDocumentAppropriation(application.id)
      .then(() => {
        toast.success('Apropriação criada com sucesso!');
        fetchAppropriationData(application.id);
      })
      .catch(() => {
        toast.error('Ocorreu um erro ao criar a apropriação.');
        setLoadingAppropriation(false);
      });
  }, [application, fetchAppropriationData]);

  const handleTransferAppropriation = useCallback((appropriationId: number, userId: number) => {
    if (!application) return;
    setLoadingAppropriation(true);
    transferTransferDocumentAppropriation(appropriationId, userId)
      .then(() => {
        toast.success('Apropriação transferida com sucesso!');
        fetchAppropriationData(application.id);
      })
      .catch(error => {
        const detail = error.response
          && error.response.data
          && error.response.data.non_field_errors
          && error.response.data.non_field_errors[0];
        toast.error(detail || 'Ocorreu um erro ao transferir a apropriação.');
        setLoadingAppropriation(false);
      });
  }, [application, fetchAppropriationData]);

  return (
    <PermissionContext.Consumer>
      {
        ({ hasPermission }: { hasPermission: (permission: string) => boolean }) => (
          <div className="transfer-review-page">
          <Card title={`Inscrição #${application ? application.id : ''} - Análise Secretaria`}>
            <Spinner active={loading}>
              {application
                && hasPermission('transfers.approve_application_document_data')
                && application.status === 'Em análise'
                && application.documentation_step_status === 'Em análise' && (
                <AppropriationAlert
                  activeAppropriation={currentAppropriation}
                  appropriations={appropriations}
                  onAppropriate={handleAppropriate}
                  loading={loadingAppropriation}
                  entityLabel="inscrição"
                />
              )}
              <FieldSet
                title="Dados Pessoais"
                action={application && hasPermission('transfers.approve_application_document_data') && !isEditingRegistration && (
                  <button
                    id="btn-edit-registration"
                    className="btn btn-black w-auto"
                    type="button"
                    onClick={() => {
                      setRegistrationForm({
                        real_name: application ? application.real_name || '' : '',
                        assumed_name: application ? application.assumed_name || '' : '',
                        email: application ? application.email || '' : '',
                      });
                      setIsEditingRegistration(true);
                    }}
                  >
                    <FontAwesomeIcon icon="edit" /> Editar
                  </button>
                )}
              />
              {isEditingRegistration && (
                <div className="enrollment-flex">
                  <div className="col-md-4">
                    <label>Nome</label>
                    <input
                      className="form-control"
                      value={registrationForm.real_name}
                      onChange={(event) => setRegistrationForm({ ...registrationForm, real_name: event.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label>Nome Social</label>
                    <input
                      className="form-control"
                      value={registrationForm.assumed_name}
                      onChange={(event) => setRegistrationForm({ ...registrationForm, assumed_name: event.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label>Email</label>
                    <input
                      className="form-control"
                      type="email"
                      value={registrationForm.email}
                      onChange={(event) => setRegistrationForm({ ...registrationForm, email: event.target.value })}
                    />
                  </div>
                  <div className="col-12 d-flex justify-content-end mt-2">
                    <button
                      id="btn-cancel-registration"
                      className="btn btn-black d-inline-flex align-items-center justify-content-center"
                      style={{ width: 40, height: 40, padding: 0 }}
                      type="button"
                      title="Cancelar"
                      onClick={() => setIsEditingRegistration(false)}
                      disabled={savingRegistration}
                    >
                      <FontAwesomeIcon icon="times" />
                    </button>
                    <button
                      id="btn-save-registration"
                      className="btn btn-primary d-inline-flex align-items-center justify-content-center ml-2"
                      style={{ width: 40, height: 40, padding: 0 }}
                      type="button"
                      title="Salvar"
                      onClick={handleSaveRegistration}
                      disabled={savingRegistration}
                    >
                      <FontAwesomeIcon icon="check" />
                    </button>
                  </div>
                </div>
              )}
              {!isEditingRegistration && (
                <div className="enrollment-flex">
                  <KeyValue title={application ? application.real_name : ''} label="Nome" />
                  <KeyValue title={application ? application.assumed_name : ''} label="Nome Social" />
                  <KeyValue title={application ? formater.cpf(application.cpf) : ''} label="CPF" />
                  <KeyValue title={application ? application.email : ''} label="Email" />
                  <KeyValue title={application ? formater.phone(application.phone) : ''} label="Telefone" />
                  <KeyValue title={application ? application.birth_date : ''} label="Data de nascimento" />
                </div>
              )}
              <FieldSet title="Processo" />
              <div className="enrollment-flex">
                <KeyValue title={application ? application.process.name : ''} label="Nome" />
                <KeyValue title={application ? application.process.course.name : ''} label="Curso" />
                <KeyValue title={`${application ? application.desired_onboarding.field_step : ''}ᵒ`} label="Período pleiteado" />
                <KeyValue title={application ? application.status : ''} label="Situação" />
                <KeyValue title={application ? application.documentation_step_status : ''} label="Aprovação Secretaria" />
                <KeyValue title={application ? application.academic_step_status : ''} label="Aprovação Coordenação" />
                {application && application.status === 'Aprovado' && application.deferred_onboarding && <KeyValue title={`${application.deferred_onboarding.field_step}ᵒ`} label="Período deferido" />}
                {application && application.status === 'Aprovado' && <KeyValue title={application.approved_by} label="Deferido por" />}
                {application && application.status === 'Aprovado' && <KeyValue title={application.enrollment ? application.enrollment.status : 'Não criada'} label="Situação matricula" />}
                {application && application.status === 'Reprovado' && <KeyValue title={application.disapproved_by} label="Indeferido por" />}
                {application && application.status === 'Reprovado' && <KeyValue col="12" title={application.disapproval_reason} label="Motivo da reprovação" />}
              </div>
              <FieldSet title="Documentos" />
              <div className="documents-base row">
                <DocumentsView
                  auditPermission={hasPermission('transfers.approve_application_document_data') && isAppropriatedByMe}
                  audittableDocuments
                  personalDocuments={application ? application.documents : []}
                  personalDocumentsTypes={requestedDocuments || []}
                  onApproveDocument={(document: IDocument & { type: IDocumentType }) => setDocumentUnderReview({ document, result: 'Approved' })}
                  onReproveDocument={(document: IDocument & { type: IDocumentType }) => setDocumentUnderReview({ document, result: 'Reproved' })}
                />
              </div>
              {application && hasPermission('transfers.approve_application_document_data') && (
                <AppropriationHistory
                  appropriations={appropriations}
                  canTransfer={canTransfer}
                  onTransfer={handleTransferAppropriation}
                  loading={loadingAppropriation}
                  entityLabel="inscrição"
                  fetchUsersApi={(search: string) => (
                    fetchEligibleTransferAppropriationUsers(application.id, search)
                  )}
                />
              )}
              {application
                && application.process.kind !== consts.TransferProcessKind.CONVOCATION
                && application.curriculum_adaptation_document && (
                <>
                  <FieldSet title="Adaptação curricular" />
                  <div className="documents-base row">
                    <DocumentsView
                      personalDocuments={application && application.curriculum_adaptation_document ? [application.curriculum_adaptation_document] : []}
                      personalDocumentsTypes={application ? [application.curriculum_adaptation_document_type] : []}
                    />
                  </div>
                </>
              )}
              {application
                && application.process.kind === consts.TransferProcessKind.CONVOCATION
                && application.process.course.name === 'Medicina'
                && coordinationDocuments.length > 0 && (
                <>
                  <FieldSet title="Documentos da Coordenação" />
                  <div className="row">
                    {coordinationDocuments.map(group => (
                      <CoordinationDocumentCard
                        key={group.kind}
                        token={application.token}
                        group={group}
                        canUpload={false}
                        onUploaded={fetchCoordinationDocumentHistory}
                      />
                    ))}
                  </div>
                  <div className="d-flex align-items-center justify-content-between flex-wrap mt-3">
                    <p className="mb-2">
                      <strong>Análise final:</strong> {finalReviewLabel(application)}
                    </p>
                    {hasPermission('transfers.approve_application_document_data')
                      && application.coordination_sent_at
                      && application.coordination_documents_step_status !== 'Aprovado' && (
                      <div className="d-flex mb-2">
                        <button
                          id="btn-coordination-issue"
                          className="btn btn-black"
                          type="button"
                          onClick={() => setShowCoordinationIssueModal(true)}
                        >
                          Pendência para a Coordenação
                        </button>
                        {/* O botão desabilitado não recebe o hover: o tooltip fica no invólucro. */}
                        <span
                          className="ml-2"
                          {...(application.coordination_documents_step_status !== 'Em análise'
                            ? { 'data-tip': true, 'data-for': 'btn-approve-coordination' }
                            : {})}
                        >
                          <button
                            id="btn-approve-coordination"
                            className="btn btn-primary"
                            type="button"
                            onClick={handleApproveCoordinationDocuments}
                            disabled={approvingCoordination || application.coordination_documents_step_status !== 'Em análise'}
                            style={application.coordination_documents_step_status !== 'Em análise' ? { pointerEvents: 'none' } : undefined}
                          >
                            {approvingCoordination ? 'Aprovando...' : 'Aprovar documentos da Coordenação'}
                          </button>
                        </span>
                        {application.coordination_documents_step_status !== 'Em análise' && (
                          <ReactTooltip id="btn-approve-coordination" className="no-arrow-tooltip">
                            {application.coordination_documents_step_status === 'Com pendências'
                              ? 'Habilita quando a Coordenação corrigir a pendência e enviar de novo para a análise final.'
                              : 'Habilita quando a Coordenação enviar a Adaptação Curricular e a Escala – Grade de Horários para a análise final.'}
                          </ReactTooltip>
                        )}
                      </div>
                    )}
                  </div>
                  {application.issues
                    .filter(p => p.category === 'Documentos da Coordenação')
                    .map(p => (
                      <div className="pendencies" key={p.id}>
                        <div className="pendencies-header">
                          <div className="mb-2">
                            <label className="pendencies-header-title">{issueLabel(p.id, p.created_by)}</label>
                            {p.status === 'Criada' && <FontAwesomeIcon icon="spinner" className="green ml-2 mt-1" />}
                            {p.status === 'Enviada' && <FontAwesomeIcon icon="envelope" className="green mt-1 ml-2" />}
                            {p.status === 'Resolvida' && <FontAwesomeIcon icon="check-circle" className="green mt-1 ml-2" />}
                          </div>
                        </div>
                        {/* pre-line: o conteúdo traz uma seção por documento, em linhas separadas. */}
                        <div className="pendencies-body" style={{ whiteSpace: 'pre-line' }}>
                          {p.content}
                        </div>
                        <div className="pendencies-date m-1">Resolvida em: {p.resolved_at || '-'}</div>
                        <div className="pendencies-date m-1">Enviada à Coordenação em: {p.sent_at || '-'}</div>
                        <div className="pendencies-date m-1">Criada em: {p.created_at}</div>
                      </div>
                    ))}
                </>
              )}
              <FieldSet title="Pendências">
              {hasPermission('transfers.approve_application_document_data') && isAppropriatedByMe && (
                <div className="mb-3">
                  <button
                    className="btn btn-black"
                    onClick={() => setShowCreateIssueModal(true)}
                    type="button"
                  >
                    <FontAwesomeIcon icon="plus" /> Nova Pendência
                  </button>
                </div>
              )}
              </FieldSet>
              {(application ? application.issues.filter(i => i.category === 'Documental') : []).length === 0 && <p>Nenhuma pendência criada.</p>}
              {(application ? application.issues.filter(i => i.category === 'Documental') : []).map(p => (
                <div className="pendencies">
                  <div className="pendencies-header">
                    <div className="mb-2">
                      <label className="pendencies-header-title">{issueLabel(p.id, p.created_by)}</label>
                      {p.status === 'Criada' && <FontAwesomeIcon icon="spinner" className="green ml-2 mt-1" />}
                      {p.status === 'Enviada' && <FontAwesomeIcon icon="envelope" className="green mt-1 ml-2" />}
                      {p.status === 'Resolvida' && <FontAwesomeIcon icon="check-circle" className="green mt-1 ml-2" />}
                      {p.document && <small className="d-block"><em>{p.document.type && p.document.type.name}</em></small>}
                    </div>
                  </div>
                  <div className="pendencies-body">
                    {p.content}
                  </div>
                  <div className="pendencies-date m-1">Resolvida em: {p.resolved_at || '-'}</div>
                  <div className="pendencies-date m-1">Enviada em: {p.sent_at || '-'}</div>
                  <div className="pendencies-date m-1">Criada em: {p.created_at}</div>
                </div>
              ))}
              <div className="d-flex justify-content-end mt-2">
                    {application && hasPermission('transfers.approve_application_document_data') && isAppropriatedByMe && application.status === 'Em análise' && application.documentation_step_status === 'Em análise' && (
                      <>
                        <button
                          data-tip
                          data-for="btn-submit"
                          id='btn-submit'
                          className={`btn btn-${analysisResult.application.value ? 'primary' : 'danger'}`}
                          onClick={() => handleReviewDocuments()}
                          disabled={!analysisResult.application.done || submiting}
                          type="submit"
                        >
                          {(analysisResult.application.value || !analysisResult.application.done) ? 'Aprovar' : 'Enviar pendências'}
                        </button>
                        {!analysisResult.application.done && (
                          <ReactTooltip id="btn-submit" className="no-arrow-tooltip">
                            Análise todos os documentos para finalizar.
                          </ReactTooltip>
                        )}
                      </>
                    )}
              </div>
              {application && hasPermission('transfers.promote_application') && application.status === 'Aprovado' && !application.enrollment && (
                <>
                  <div className="d-flex justify-content-end mt-2">
                    <button
                      id='btn-submit'
                      className={`btn btn-primary`}
                      onClick={() => setShowEnrollmentCreationModal(true)}
                      disabled={submiting}
                    >
                      Gerar matricula
                    </button>
                  </div>
                  <CreateEnrollmentModal onCreate={handleCreateEnrollment} onClose={() => setShowEnrollmentCreationModal(false)} show={showEnrollmentCreationModal} application={application} />
                </>
              )}
            </Spinner>
            {(documentUnderReview && application) && (
              <TransferApplicationDocumentAnalysisModal
                onConfirm={() => {
                  setDocumentUnderReview(undefined);
                  fetchApplication();
                }}
                application={application}
                show={!!documentUnderReview}
                document={documentUnderReview.document}
                analysisResult={documentUnderReview.result}
                onClose={() => setDocumentUnderReview(undefined)}
              />
            )}
            {application && (
              <CreateIssueModal
                show={showCreateIssueModal}
                application={application}
                onClose={() => setShowCreateIssueModal(false)}
                onCreate={handleCreateIssue}
              />
            )}
            {application && (
              <CoordinationIssueModal
                show={showCoordinationIssueModal}
                application={application}
                onClose={() => setShowCoordinationIssueModal(false)}
                onCreate={handleCreateCoordinationIssue}
              />
            )}
          </Card>
          </div>
        )}
    </PermissionContext.Consumer>
  )
}

export default TransferApplicationDocumentReviewPage;
